import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {EntityType} from '@lib/models/entity/entity-type';
import {INewsPost} from '@api/models/news/news';
import {Event} from '@api/models/event/event';
import {LeagueService} from '@core/services/league.service';
import {TeamService} from '@core/services/team.service';
import {ContentCardLayout} from '@lib-modules/cards/components/content-card/content-card.component';
import {PickerItem, PickerItemType} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {EventCardService, EventContentType} from '@lib-modules/cards/services/event-card/event-card.service';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {StateService} from '@core/services/state.service';
import {NewsCardService, NewsContentType} from '@lib-modules/cards/services/news-card/news-card.service';
import {VideoCardService, VideoContentType} from '@lib-modules/cards/services/video-card/video-card.service';

@Component({
  selector: 'lib-content-card-list',
  templateUrl: './content-card-list.component.html',
  styleUrls: ['./content-card-list.component.scss']
})
export class ContentCardListComponent implements OnChanges, OnInit {

  @ViewChild('cardList') cardList: ElementRef<HTMLDivElement>;
  @Input() item: PickerItem;
  @Input() colorScheme = ColorScheme.Dark;
  @Input() newsType: NewsContentType;
  @Input() videoType: VideoContentType;
  @Input() public pageSize = 16;
  @Input() public adOffset = 3;
  @Input() public backLink: string;
  public moreContent = false;
  public layout = ContentCardLayout.Stacked;
  public loading = false;
  public isWebView = StateService.isWebview;
  public id: string;
  public news: INewsPost[] = [];
  public matches: Event[] = [];

  protected readonly EntityType = EntityType;
  protected readonly PickerItemType = PickerItemType;
  protected readonly VideoContentType = VideoContentType;

  public constructor(
    public leagueService: LeagueService,
    public teamService: TeamService,
    private videoCardService: VideoCardService,
    private newsCardService: NewsCardService,
    private analyticsService: AnalyticsService) {
  }

  public ngOnInit() {
    this.loading = true;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.item || changes.newsType) {
      await this.refreshNews();
    }
  }

  private async refreshNews() {
    this.showCardList(false);
    setTimeout(async () => {
      this.loading = true;
      this.news = [];
      await this.loadMoreContent();
      this.loading = false
      setTimeout(() => {
        this.showCardList(true);
      }, 150);
    }, 150);
  }

  public onMoreClick() {
    this.analyticsService.sendPageView();
    this.loadMoreContent();
  }

  private async loadMoreContent() {
    if (this.newsType) {
      const response = await this.newsCardService.fetchData(
        this.newsType, this.item, this.news.length, this.pageSize);
      this.moreContent = response.length === this.pageSize;
      this.news = this.news.concat(response);
    } else if (this.videoType) {
      const response = await this.videoCardService.fetchData(
        this.videoType, this.item, null, this.matches.length, this.pageSize);
      this.moreContent = response.length === this.pageSize;
      this.matches = this.matches.concat(response);
    }
  }

  private showCardList(show: boolean) {
    if (this.cardList && this.cardList.nativeElement) {
      if (show) {
        this.cardList.nativeElement.classList.add('fade-in');
      } else {
        this.cardList.nativeElement.classList.remove('fade-in');
      }
    }
  }

  public get moreButtonTitle(): string {
    if (this.newsType) {
      switch (this.newsType) {
        case NewsContentType.EntityNews:
          return 'MEHR NEWS';
        case NewsContentType.VideoNews:
          return 'MEHR VIDEOS';
      }
    }
    if (this.videoType) {
      return 'MEHR VIDEOS'
    }
    return null;
  }

  // Show ad after first row of pagination (first 4 content cards)
  // TODO: find better solution
  public showAd(index: number): boolean {
    if (index === 0) {
      return false;
    }
    return (index - this.adOffset) % this.pageSize === 0;
  }

  public get placeholderText(): string {
    const entity = this.item.type === PickerItemType.League ? 'dieser Liga' : 'diesem Team';
    if (this.news.length === 0 && this.matches.length === 0) {
      if (this.newsType === NewsContentType.EntityNews) {
        return `Leider gibt es zu ${entity} noch keine News`;
      }
      return `Leider gibt es zu ${entity} noch keine Videos`;
    }
    return null;
  }
}
